/**
 * Vouchers — the codes under whichever event is open in the console, the
 * list's view-model, the operator's in-progress draft shape, and the two
 * intent channels every voucher-CRUD view dispatches through.
 *
 * `vouchersEventId$` is the side-channel `<event-console>` pushes its open
 * event's id into; vouchers.sync.ts (web-side) is the sole subscriber of that
 * channel, of `vouchersAction$` (performing the write, reconciled back via
 * `vouchersResult$`), and of `redemptionsRequest$` (listing one code's
 * redemption documents and joining each to its order for a name, answered on
 * `redemptionsResult$`) — all three keyed by requestId. Same shape
 * `events/ticket-types` already uses.
 *
 * A voucher document is member-read behind `tickets.vouchers.manage` + mfa
 * and readable by no buyer at any tier (voucher.entity.ts), so nothing in
 * this file is ever mounted on a storefront surface.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Voucher } from "@momo/types";
import { BehaviorSubject, Subject } from "rxjs";
import { activeActor } from "../auth/actor.state.ts";
import { ticketTypesState } from "../events/ticket-types/ticket-types.state.ts";

/** The stored body rejoined to the doc id it was read at — `resolveVoucher`'s
 *  own input shape, and the one representation of the code anywhere on the
 *  client, since the document deliberately stores no copy of it. */
export type VoucherWithCode = Voucher.Voucher & { code: string };

// ── Live state singletons ───────────────────────────────────────────────────

/** Which event's vouchers subcollection is currently listened to — null closes
 *  the listener. */
export const vouchersEventId$ = new BehaviorSubject<string | null>(null);

export const vouchersState = state<readonly VoucherWithCode[]>(
	"events/vouchers",
).memory([]);

export const vouchersStatus = state<"loading" | "ready" | "denied" | "error">(
	"events/vouchersStatus",
).memory("loading");

/**
 * Whether the signed-in actor may mint, edit and delete codes — mirrors
 * voucher.entity.ts's one clause (tickets.vouchers.manage), which gates the
 * read face and the write face alike. The mfa half that clause also carries
 * is already true of anyone inside the admin shell (session.state.ts's
 * `sessionReady`), so restating it here would gate on a fact the shell has
 * already proved. Gates the console's Codes destination and every write
 * surface behind it.
 */
export const canManageVouchers = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.vouchers.manage") ?? false,
);

// ── Page view-model ─────────────────────────────────────────────────────────

/** What a code DOES, as facts — the sentence is the card's, because the words
 *  are the locale's. Both halves may be absent on one code and present on
 *  another; the schema refuses only a code carrying neither. */
export type VoucherEffect = {
	readonly discount: NonNullable<Voucher.Voucher["discount"]> | null;
	/** The types this code reveals, by name. */
	readonly unlockNames: readonly string[];
	/** How many it reveals, including any whose name no longer resolves — a
	 *  type deleted after the code was written. The two disagree only there,
	 *  and that is the one case the card may not print names for. */
	readonly unlockCount: number;
	/** The types the discount is confined to, by name. Empty count is the
	 *  schema's own "absent means unconstrained": every type. */
	readonly scopeNames: readonly string[];
	readonly scopeCount: number;
	/** The discount is confined to exactly the types this code reveals — the
	 *  partner idiom, where "20 %" means "20 % on the tier this code opens" and
	 *  naming the type twice in one sentence would say it twice. */
	readonly scopeIsUnlocks: boolean;
};

/** Why a code can no longer apply. Expiry outranks exhaustion because it
 *  carries a date the card states nowhere else, while exhaustion is already
 *  spelled out by the two figures inside the code's own meter. */
export type VoucherStop = "expired" | "spent";

/** One code as <voucher-card> renders it. */
export type VoucherCardRow = {
	readonly code: string;
	readonly label: Voucher.Voucher["label"];
	readonly effect: VoucherEffect;
	readonly usedCount: Voucher.Voucher["usedCount"];
	readonly usageLimit: Voucher.Voucher["usageLimit"];
	readonly expiresAtMs: number | null;
	readonly stop: VoucherStop | null;
};

/**
 * The whole list surface in one value, read whole by <voucher-list>.
 *
 * ONE CLOCK FOR THE WHOLE SCREEN. `stop` is decided here rather than
 * re-decided per card against a fresh `Date.now()`, so no two cards can
 * disagree about which side of an expiry the screen is on, and a re-render
 * fired by an unrelated keystroke cannot silently restate a card.
 *
 * `unlocks` and `constraints.typeIds` resolve to ticket-type NAMES. The
 * question a list of codes is asked is what each one does, and a count of
 * hidden tickets answers a different one. The names are already in the room:
 * <event-console> opens `ticketTypesEventId$` for the whole console, not just
 * this destination.
 */
export const vouchersPageView = computed(() => {
	const types = ticketTypesState.value;
	const nowMs = Date.now();
	const cards = vouchersState.value
		.map((v) => {
			const unlocks = v.unlocks ?? [];
			const scope = v.constraints.typeIds ?? [];
			const expiresAtMs = v.constraints.validUntil?.toMillis() ?? null;
			return {
				createdAtMs: v.createdAt.toMillis(),
				card: {
					code: v.code,
					label: v.label,
					effect: {
						discount: v.discount ?? null,
						unlockNames: unlocks.flatMap(
							(id) => types.find((t) => t.id === id)?.name ?? [],
						),
						unlockCount: unlocks.length,
						scopeNames: scope.flatMap(
							(id) => types.find((t) => t.id === id)?.name ?? [],
						),
						scopeCount: scope.length,
						scopeIsUnlocks:
							scope.length > 0 &&
							scope.length === unlocks.length &&
							scope.every((id) => unlocks.includes(id)),
					},
					usedCount: v.usedCount,
					usageLimit: v.usageLimit,
					expiresAtMs,
					stop:
						expiresAtMs !== null && expiresAtMs < nowMs
							? ("expired" as const)
							: v.usedCount >= v.usageLimit
								? ("spent" as const)
								: null,
				},
			};
		})
		// Newest first: an operator opening this list has almost always just
		// minted the code they came back for. Ties fall to the code itself, so
		// two codes issued in the same second never trade places between
		// renders. A code that reaches its limit or its expiry keeps its place
		// rather than sinking to a stopped band — the code an operator is
		// hunting is most often the one that just stopped working, and a card
		// that moves when a counter reaches its limit moves under the cursor of
		// whoever is watching it.
		.toSorted(
			(a, b) =>
				b.createdAtMs - a.createdAtMs || a.card.code.localeCompare(b.card.code),
		);

	return {
		status: vouchersStatus.value,
		/** Every code, newest first. */
		cards: cards.map((row) => row.card),
	};
});

/** Which of this event's codes reveal `typeId` — the fact the ticket-type
 *  form's audience answer is uniquely placed to state, since vouchers live
 *  under the event and are therefore one already-open listener away. Empty
 *  means a code-gated type nobody can reach. */
export function unlockingCodes(typeId: string): readonly string[] {
	return vouchersState.value
		.filter((v) => (v.unlocks ?? []).includes(typeId))
		.map((v) => v.code);
}

// ── The operator's draft ────────────────────────────────────────────────────

/**
 * A draft is genuinely not a `Voucher`: the code is empty, neither half is
 * answered yet, and the whole point of the form is the window in which it is
 * not valid. `z.input<typeof VoucherSchema>` is not that shape either — a
 * field the operator leaves empty is ABSENT on the document and has to be
 * SOMETHING on a form: zero for a half that takes nothing off and for an
 * unlimited per-person cap, an empty array for unconstrained, a wall-clock
 * string for a `Timestamp`. What it CAN derive from the canonical schema it
 * derives.
 */
export type VoucherDraft = {
	readonly code: string;
	readonly label: Voucher.Voucher["label"];
	readonly discountType: NonNullable<Voucher.Voucher["discount"]>["type"];
	/** Zero IS the off state. There is no switch beside these: a switch is a
	 *  control whose whole job is to move everything below it, and a code with
	 *  no discount is exactly a code whose amount is empty. */
	readonly percent: number;
	readonly fixedCents: number;
	/** Empty means unconstrained — the schema's own "absent is unconstrained".
	 *  There is no event scope beside it: the document lives under the event,
	 *  so the path already answers which event this code belongs to and no
	 *  control may ask again. */
	readonly typeIds: readonly string[];
	readonly unlocks: readonly string[];
	readonly usageLimit: Voucher.Voucher["usageLimit"];
	/** Zero is "unlimited per person" — the schema's own absent value. */
	readonly perBuyerLimit: number;
	/** A naive wall-clock instant, `YYYY-MM-DDTHH:mm`, or empty for no expiry,
	 *  read in the venue's own zone. NOT a bare date: `constraints.validUntil`
	 *  is a `Timestamp`, so somebody has to say which second of which day the
	 *  code dies — a day alone leaves a buyer refused on the last day by the
	 *  one refusal that is nobody's mistake and that the storefront may never
	 *  explain. */
	readonly validUntil: string;
};

export const EMPTY_DRAFT: VoucherDraft = {
	code: "",
	label: "",
	discountType: "percent",
	percent: 0,
	fixedCents: 0,
	typeIds: [],
	unlocks: [],
	usageLimit: 100,
	perBuyerLimit: 0,
	validUntil: "",
};

export type FormBlock =
	/** No code at all, or one the doc id cannot be. */
	| "code"
	/** No label. `VoucherSchema` requires one — a code nobody can tell apart in
	 *  the list six months from now is a code nobody dares delete. */
	| "label"
	/** Neither half answered: a code that does nothing, which the schema's own
	 *  refinement refuses. */
	| "job"
	/** 100 % — the one block that has to teach the other idiom. */
	| "hundredPercent";

const CODE_SHAPE = /^[A-Z0-9-]{4,32}$/;

/** Whether this draft carries a money half at all. An amount above zero IS the
 *  answer — there is no separate flag for a reader to disagree with. */
export function discountLands(draft: VoucherDraft): boolean {
	return draft.discountType === "percent"
		? draft.percent > 0
		: draft.fixedCents > 0;
}

/** Every way this draft is not yet a code, in one answer, so the footer's save
 *  gate and each field's own message can never disagree about whether the form
 *  is done — and so the one refusal that has to TEACH rather than merely block
 *  is a first-class member rather than a special case buried in a template. */
export function formBlocks(draft: VoucherDraft): readonly FormBlock[] {
	const blocks: FormBlock[] = [];
	if (!CODE_SHAPE.test(draft.code)) blocks.push("code");
	if (draft.label.trim() === "") blocks.push("label");
	if (!discountLands(draft) && draft.unlocks.length === 0) blocks.push("job");
	if (draft.discountType === "percent" && draft.percent >= 100)
		blocks.push("hundredPercent");
	return blocks;
}

// Ambiguity is the whole cost of a code read aloud down a phone line or typed
// off a printed lanyard, so the alphabet drops every glyph that pairs with
// another: no O/0, no I/1, no S/5, no B/8.
const ALPHABET = "ACDEFGHJKLMNPQRTUVWXYZ2346798";

/** Mint a one-to-one code — 10 glyphs of `crypto.getRandomValues` entropy over
 *  the unambiguous alphabet above. The caller sets the usage limits it implies
 *  (a generated code is by definition for one person) on fields the operator
 *  can still change: a silent default would be the form deciding something it
 *  never said out loud. */
export function generateCode(): string {
	return Array.from(
		crypto.getRandomValues(new Uint8Array(10)),
		(byte) => ALPHABET[byte % ALPHABET.length],
	).join("");
}

// ── write-intent channel ────────────────────────────────────────────────────

/** One code as the operator composed it — naive `Date`s and plain nulls, never
 *  a `Timestamp`, which this zero-I/O layer cannot mint. Absence is `null`
 *  throughout: the schema reads an absent half, an absent scope and an absent
 *  per-person cap as the unconstrained answer. */
export type VoucherWrite = {
	label: Voucher.Voucher["label"];
	discount: NonNullable<Voucher.Voucher["discount"]> | null;
	unlocks: readonly string[] | null;
	constraints: {
		typeIds: readonly string[] | null;
		validUntil: Date | null;
	};
	usageLimit: Voucher.Voucher["usageLimit"];
	perBuyerLimit: number | null;
};

export type VouchersAction =
	| {
			kind: "create";
			requestId: string;
			eventId: string;
			/** The doc id — a code is created, never renamed. */
			code: string;
			/** Who gave money away. The uid this view already holds; the store
			 *  seam mints only the clock. */
			createdBy: string;
			body: VoucherWrite;
	  }
	| {
			kind: "update";
			requestId: string;
			eventId: string;
			code: string;
			body: VoucherWrite;
	  }
	| { kind: "delete"; requestId: string; eventId: string; code: string };

export const vouchersAction$ = new Subject<VouchersAction>();

export interface VouchersResult {
	readonly requestId: string;
	readonly kind: VouchersAction["kind"];
	readonly ok: boolean;
}

export const vouchersResult$ = new Subject<VouchersResult>();

// ── read-intent channel — one code's own redemption list ────────────────────

/** Ask for one code's redemption documents, each joined to the order that
 *  redeemed it. Answered on `redemptionsResult$`, keyed by requestId. */
export interface RedemptionsRequest {
	readonly requestId: string;
	readonly eventId: string;
	readonly code: string;
}

export const redemptionsRequest$ = new Subject<RedemptionsRequest>();

/** One person's redemptions of one code. The document id is a hash of their
 *  email and the body stores no email at all, so the name and the address are
 *  the join through `lastOrderId` — null where that order could not be read,
 *  never a fabricated stand-in. */
export interface RedemptionRow {
	readonly buyerKey: string;
	readonly count: number;
	readonly lastOrderId: string;
	readonly lastRedeemedAtMs: number;
	readonly name: string | null;
	readonly email: string | null;
}

export interface RedemptionsResult {
	readonly requestId: string;
	/** Null when the list itself could not be read — a denied or failed query.
	 *  An empty array is the other fact entirely: a code nobody has redeemed. */
	readonly rows: readonly RedemptionRow[] | null;
}

export const redemptionsResult$ = new Subject<RedemptionsResult>();
